// Defect list side panel (search + severity/type filters + rows).
import { useState } from "react";
import { IcSearch } from "./icons";
import {
  CatFilter, catOf, CategoryTag, ConfidenceMeter, SeverityPill, sevMeta, SevFilter, SourceTag,
  type CatFilterValue, type SevFilterValue,
} from "./meta";
import type { Defect } from "./types";
import type { ReactNode } from "react";

function DefectRow({ d, selected, onClick, compact }: { d: Defect; selected: boolean; onClick: () => void; compact?: boolean }) {
  const [h, setH] = useState(false);
  const m = sevMeta(d.severity);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", padding: compact ? "9px 12px" : "11px 13px", border: "none", borderLeft: `2.5px solid ${selected ? "var(--di-orange)" : m.color}`, background: selected ? "rgba(249,115,22,0.08)" : h ? "rgba(255,255,255,0.03)" : "transparent", cursor: "pointer", textAlign: "left", transition: "background 120ms ease" }}>
      <div style={{ width: 26, height: 26, borderRadius: "50%", background: "var(--di-orange)", border: "1.5px solid #160d03", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: "#160d03" }}>{d.ref}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--di-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.title}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
          <SeverityPill sev={d.severity} size="sm" /><CategoryTag cat={catOf(d)} size="sm" /><SourceTag d={d} />
        </div>
      </div>
      {d.source === "ai" && <ConfidenceMeter value={d.confidence} variant="row" />}
    </button>
  );
}

export function DefectList({ defects, selectedId, onSelect, search, setSearch, filterSev, setFilterSev, counts, filterCat, setFilterCat, catCounts, compact, headerRight }: {
  defects: Defect[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  search: string;
  setSearch: (s: string) => void;
  filterSev: SevFilterValue;
  setFilterSev: (v: SevFilterValue) => void;
  counts: Partial<Record<string, number>>;
  filterCat?: CatFilterValue;
  setFilterCat?: (v: CatFilterValue) => void;
  catCounts?: Partial<Record<string, number>>;
  compact?: boolean;
  headerRight?: ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ padding: compact ? "14px 14px 12px" : "16px 16px 13px", borderBottom: "1px solid var(--di-border)", display: "flex", flexDirection: "column", gap: 12, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.01em" }}>Defects</span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: "var(--di-orange)" }}>{defects.length}</span>
          </div>
          {headerRight}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, height: 36, padding: "0 11px", borderRadius: 9, background: "var(--di-navy)", border: "1px solid var(--di-border)" }}>
          <IcSearch size={13} color="var(--di-subtle)" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search defects…"
            style={{ flex: 1, border: "none", background: "transparent", outline: "none", color: "var(--di-text)", fontFamily: "var(--font-sans)", fontSize: 13 }} />
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <SevFilter value={filterSev} setValue={setFilterSev} counts={counts} />
          {setFilterCat && <CatFilter value={filterCat ?? "all"} setValue={setFilterCat} counts={catCounts ?? {}} />}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {defects.length === 0 && <div style={{ padding: "40px 16px", textAlign: "center", fontSize: 13, color: "var(--di-subtle)" }}>No defects match.</div>}
        {defects.map((d) => <DefectRow key={d.id} d={d} selected={d.id === selectedId} compact={compact} onClick={() => onSelect(d.id)} />)}
      </div>
    </div>
  );
}
